import { useState, useRef, useEffect, useMemo } from 'react'
import { MessageSquare, Plus, Trash2, RefreshCw, Pencil, Check, X, Download, Search } from 'lucide-react'
import clsx from 'clsx'
import type { ConversationSummary } from '../types'

const PAGE_SIZE = 20

export interface HistorySidebarProps {
  conversationHistory: ConversationSummary[]
  activeConversationId: string | null
  isLoadingHistory: boolean
  deletingConversationId: string | null
  onOpenConversation: (id: string) => void
  onDeleteConversation: (id: string) => void
  onRenameConversation: (id: string, title: string) => void
  onExportConversation: (id: string) => void
  onNewChat: () => void
}

function groupConversations(conversations: ConversationSummary[]) {
  const now = new Date()
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const startOfYesterday = new Date(startOfToday)
  startOfYesterday.setDate(startOfYesterday.getDate() - 1)

  return conversations.reduce<Array<{ label: string; items: ConversationSummary[] }>>((groups, conversation) => {
    const updatedAt = new Date(conversation.updated_at)
    const label =
      updatedAt >= startOfToday
        ? 'Today'
        : updatedAt >= startOfYesterday
          ? 'Yesterday'
          : 'Earlier'

    const existing = groups.find((group) => group.label === label)
    if (existing) {
      existing.items.push(conversation)
      return groups
    }

    groups.push({ label, items: [conversation] })
    return groups
  }, [])
}

function ConversationItem({
  conversation,
  isActive,
  isDeleting,
  onOpen,
  onDelete,
  onRename,
  onExport,
}: {
  conversation: ConversationSummary
  isActive: boolean
  isDeleting: boolean
  onOpen: () => void
  onDelete: () => void
  onRename: (title: string) => void
  onExport: () => void
}) {
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const title = conversation.title?.trim() || 'Untitled conversation'

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  const startEditing = (e: React.MouseEvent) => {
    e.stopPropagation()
    setEditValue(title)
    setIsEditing(true)
  }

  const saveEdit = () => {
    const trimmed = editValue.trim()
    if (trimmed && trimmed !== title) {
      onRename(trimmed)
    }
    setIsEditing(false)
  }

  const cancelEdit = () => {
    setIsEditing(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      saveEdit()
    } else if (e.key === 'Escape') {
      cancelEdit()
    }
  }

  return (
    <div
      className={clsx(
        'group rounded-lg border transition cursor-pointer',
        isActive
          ? 'border-accent/30 bg-accent-soft'
          : 'border-transparent bg-card hover:border-subtle hover:bg-foreground/[0.04]',
      )}
    >
      <div className="flex items-center justify-between gap-1 px-3 py-2">
        {isEditing ? (
          <div className="flex items-center gap-1 flex-1 min-w-0">
            <input
              ref={inputRef}
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={saveEdit}
              className="flex-1 min-w-0 text-xs font-medium text-foreground bg-card border border-accent/40 rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-ring"
              maxLength={80}
            />
            <button
              onMouseDown={(e) => { e.preventDefault(); saveEdit() }}
              className="p-0.5 text-success-foreground hover:text-success-foreground"
              title="Save"
            >
              <Check className="w-3.5 h-3.5" />
            </button>
            <button
              onMouseDown={(e) => { e.preventDefault(); cancelEdit() }}
              className="p-0.5 text-subtle-foreground hover:text-muted-foreground"
              title="Cancel"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : (
          <>
            <button
              onClick={onOpen}
              className="min-w-0 flex-1 text-left"
            >
              <p className="text-xs font-medium text-foreground line-clamp-1">{title}</p>
            </button>
            <div className="flex items-center gap-0.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition">
              <button
                onClick={startEditing}
                className="rounded p-1 text-subtle-foreground hover:bg-card hover:text-accent transition"
                title="Rename"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onExport() }}
                className="rounded p-1 text-subtle-foreground hover:bg-card hover:text-accent transition"
                title="Export as markdown"
                aria-label="Export conversation as markdown"
              >
                <Download className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => void onDelete()}
                disabled={isDeleting}
                className="rounded p-1 text-subtle-foreground hover:bg-card hover:text-destructive-foreground transition disabled:text-disabled-foreground disabled:cursor-not-allowed"
                title="Delete"
              >
                {isDeleting ? (
                  <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Trash2 className="h-3.5 w-3.5" />
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export function HistorySidebar({
  conversationHistory,
  activeConversationId,
  isLoadingHistory,
  deletingConversationId,
  onOpenConversation,
  onDeleteConversation,
  onRenameConversation,
  onExportConversation,
  onNewChat,
}: HistorySidebarProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)

  // Filter on title (case-insensitive); keep server order so "Today/Yesterday/
  // Earlier" buckets remain chronological after filtering.
  const filteredHistory = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return conversationHistory
    return conversationHistory.filter((c) =>
      (c.title || 'Untitled conversation').toLowerCase().includes(q),
    )
  }, [conversationHistory, searchQuery])

  // Reset paging back to first page when the filter changes — otherwise a
  // narrow filter can leave the user looking at "Show more" with no items.
  useEffect(() => {
    setVisibleCount(PAGE_SIZE)
  }, [searchQuery])

  const visibleHistory = useMemo(
    () => filteredHistory.slice(0, visibleCount),
    [filteredHistory, visibleCount],
  )
  const hasMore = filteredHistory.length > visibleCount
  const groupedConversationHistory = groupConversations(visibleHistory)

  return (
    <aside
      className={clsx(
        'hidden w-72 shrink-0 border-r border-subtle bg-card xl:flex xl:flex-col',
      )}
    >
      {/* Header — height matches ChatHeader (py-4 + 48px icon row = 80px content) */}
      <div className="sticky top-0 border-b border-subtle bg-card px-3 flex items-center" style={{ height: '81px' }}>
        <div className="flex items-center justify-between w-full">
          <p className="font-mono text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">// conversation history</p>
          {isLoadingHistory && <RefreshCw className="h-3.5 w-3.5 animate-spin text-subtle-foreground" />}
        </div>
      </div>

      {/* Search */}
      {conversationHistory.length > 0 && (
        <div className="px-3 pt-3">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-subtle-foreground" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search conversations"
              className="w-full pl-8 pr-2 py-1.5 text-xs text-foreground bg-background border rounded-md placeholder:text-subtle-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
            />
          </div>
        </div>
      )}

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto px-3 py-4">
        {conversationHistory.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-subtle bg-foreground/[0.04] px-4 py-6 text-center">
            <MessageSquare className="mx-auto mb-3 h-8 w-8 text-subtle-foreground" />
            <p className="text-sm font-medium text-foreground">No saved conversations</p>
            <p className="mt-1 text-xs text-muted-foreground">Chats you start in TestOps Hub will appear here.</p>
          </div>
        ) : filteredHistory.length === 0 ? (
          <p className="px-2 py-4 text-xs text-muted-foreground text-center">
            No conversations match &ldquo;{searchQuery}&rdquo;.
          </p>
        ) : (
          <div className="space-y-4">
            {groupedConversationHistory.map((group) => (
              <div key={group.label}>
                <p className="mb-2 px-2 font-mono text-[10px] font-semibold uppercase tracking-widest text-subtle-foreground">
                  // {group.label.toLowerCase()}
                </p>
                <div className="space-y-1">
                  {group.items.map((conversation) => (
                    <ConversationItem
                      key={conversation.id}
                      conversation={conversation}
                      isActive={conversation.id === activeConversationId}
                      isDeleting={deletingConversationId === conversation.id}
                      onOpen={() => onOpenConversation(conversation.id)}
                      onDelete={() => onDeleteConversation(conversation.id)}
                      onRename={(title) => onRenameConversation(conversation.id, title)}
                      onExport={() => onExportConversation(conversation.id)}
                    />
                  ))}
                </div>
              </div>
            ))}
            {hasMore && (
              <button
                onClick={() => setVisibleCount((n) => n + PAGE_SIZE)}
                className="w-full mt-2 px-3 py-2 text-xs font-medium text-accent-soft-foreground hover:text-accent hover:bg-accent-soft rounded-md transition"
              >
                Show {Math.min(PAGE_SIZE, filteredHistory.length - visibleCount)} more ({filteredHistory.length - visibleCount} remaining)
              </button>
            )}
          </div>
        )}
      </div>

      {/* New Chat Button at bottom - Fixed */}
      <div className="border-t border-subtle px-4 py-4 sticky bottom-0 bg-card z-10">
        <button
          onClick={onNewChat}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-accent px-4 py-3 text-sm font-medium text-white transition hover:bg-accent/90"
        >
          <Plus className="h-4 w-4" />
          New Chat
        </button>
      </div>
    </aside>
  )
}
